/**
 * Wire format of `GET /snapshot` and `GET /stream`.
 *
 * Declared here rather than imported from the engine package on purpose: the
 * engine's module graph pulls in `node:http` and `node:fs`, which must never be
 * reachable from a browser bundle. The cost is that this file can drift from
 * `packages/engine/src/loop/engine.ts` — so every field the dashboard reads is
 * used defensively, and a missing field degrades to a dash instead of throwing.
 */

export interface JudgmentView {
  tier: string;
  source: string;
  model: string;
  latencyMs: number;
  ageMs: number;
  requestId: string;
  warnings: string[];
  readings: Record<
    string,
    {
      type: string;
      value: number | string;
      probabilities?: Record<string, number>;
      confidence: number;
      detail?: string;
    }
  >;
}

export interface ThresholdView {
  value: number;
  z: number;
  effective: number;
  threshold: number;
  triggered: boolean;
  /** Which test fired: the relative (z) one, the non-adaptive floor, or neither. */
  firedBy: "z" | "absolute" | null;
  absoluteThreshold: number;
}

export interface ActionView {
  kind: string;
  bid: number | null;
  ask: number | null;
  size: number;
  reason: string;
  source: string;
  late: boolean;
}

export interface Snapshot {
  running: boolean;
  symbol: string;
  venue: string;
  /** The venue's own instrument name, e.g. "HYPE" for a Hyperliquid perp. */
  instrument?: string | null;
  uptimeMs: number;
  block: number;
  mid: number;
  bestBid: number;
  bestAsk: number;
  spreadBps: number;
  features: Record<string, number> | null;
  snapshot: Record<string, number | string> | null;
  state: string;

  action: ActionView | null;
  risk: { verdict: string; reasons: string[]; primary: string } | null;
  ladder: { rung: string; reason: string; mayQuote: boolean } | null;

  fast: JudgmentView | null;
  full: JudgmentView | null;
  /** 第二模型（Jev）狀態與閘門判定。null = 引擎未啟用雙模型閘門。 */
  jev?: {
    /** 雙模型閘門係咪開緊。 */
    enabled?: boolean;
    model: string;
    ageMs: number;
    latencyMs: number;
    consecutiveErrors: number;
    gate: { positive: boolean; reason: string } | null;
    /** Jev 嘅原始讀數，用嚟同 Laya 對照。 */
    view?: JudgmentView | null;
    /** 距離下一次 Jev 呼叫仲有幾毫秒。 */
    nextCallInMs?: number;
    /** Jev 判斷嘅有效期。 */
    maxAgeMs?: number;
  } | null;
  thresholds: Record<string, ThresholdView> | null;
  sizing: { size: number; terms: Record<string, number> } | null;

  portfolio: {
    position: number;
    cash: number;
    markPrice: number;
    peakEquity: number;
    equity: number;
    realizedPnl: number;
    dayPnl: number;
    inventoryAgeMs: number;
  };

  /** P&L decomposition. `reconciliationGap` must be ~0. */
  pnl: {
    realized: number;
    unrealized: number;
    fees: number;
    /** Realised on closing fills BEFORE fees — what the trades earned on their own. */
    grossRealized?: number;
    /** The account's opening balance; total P&L is equity minus this. */
    startingCash?: number;
    avgEntry: number;
    reconciliationGap: number;
  };

  /**
   * Lifetime trade counters. Absent from engines started before these existed,
   * and `known: false` for an account resumed from a session saved back then —
   * in both cases the feed below is only a slice and the totals are not
   * recoverable, so they must render as unknown rather than as the slice's size.
   */
  trades?: {
    known: boolean;
    fills: number;
    buys: number;
    sells: number;
    buyVolume: number;
    sellVolume: number;
  };

  restingOrders: Array<{ side: string; price: number; size: number }>;
  fills: Array<{ tsMs: number; seq: number; side: string; price: number; size: number; fee: number; realized: number }>;
  equityCurve: Array<{ tsMs: number; equity: number; mid: number }>;

  baseline: Record<string, { n: number; mean: number; sd: number; min: number; max: number; p10: number; p90: number }>;
  stats: {
    ticks: number;
    decisions: number;
    fills: number;
    modelCalls: number;
    modelErrors: number;
    fallbackJudgments: number;
    lateTicks: number;
    lastLatencyMs: number;
    avgLatencyMs: number;
    startedAtMs: number;
  };
  client: {
    baseUrl: string;
    model: string | null;
    inFlight: number;
    queued: number;
    calls: number;
    timeouts: number;
    failures: number;
    avgLatencyMs: number;
  };
  calibration: { pending: number; failures: number; path: string };
  config: {
    tickMs: number;
    fastMinIntervalMs: number;
    fullMinIntervalMs: number;
    maxFastAgeMs: number;
    maxFullAgeMs: number;
    thresholdMode: string;
    dryRun: boolean;
  };
}

/** The six questions, in the order the article's battery presents them. */
export const QUESTION_ORDER = [
  "regime",
  "direction",
  "toxic_flow",
  "liquidity_stressed",
  "quote_environment",
  "inventory_pressure",
] as const;

export const QUESTION_LABELS: Record<string, string> = {
  regime: "市況",
  direction: "方向",
  toxic_flow: "有毒流量",
  liquidity_stressed: "流動性緊張",
  quote_environment: "報價環境",
  inventory_pressure: "持倉壓力",
};

// ---------------------------------------------------------------------------
// Display vocabulary
//
// The wire carries codes, not prose: rungs, verdicts, action kinds and the
// model's own answer words are used by the engine's logic, so they are mapped
// here at the edge rather than translated at the source. The model's answer
// words in particular are its own vocabulary — translating them on the wire
// would change what the model is asked and invalidate the calibration.
// ---------------------------------------------------------------------------

/** Threshold readings that are not one of the six questions. */
export const METRIC_LABELS: Record<string, string> = {
  quote_environment_wide: "報價環境（擴闊）",
};

/** The model's answer words, as shown in the answer column and the distribution bars. */
export const ANSWER_LABELS: Record<string, string> = {
  trending: "趨勢",
  mean_reverting: "均值回歸",
  high_vol: "高波動",
  crisis: "危機",
  up: "上升",
  down: "下跌",
  neutral: "中性",
  true: "是",
  false: "否",
};

/** Score-scale answers, which are indices into each question's own option list. */
export const SCORE_LABELS: Record<string, Record<string, string>> = {
  quote_environment: { "0": "不宜報價", "1": "一般（窄幅小量）", "2": "標準", "3": "極佳" },
  inventory_pressure: { "0": "無壓力", "1": "輕微", "2": "嚴重", "3": "立即減倉" },
};

export const RUNG_LABELS: Record<string, string> = {
  RUN: "正常報價",
  REDUCE: "縮減",
  HOLD: "暫停報價",
  FALLBACK: "後備判斷",
  STALE: "判斷過期",
  KILL: "終止",
};

export const ACTION_LABELS: Record<string, string> = {
  quote: "報價",
  widen: "擴闊報價",
  reduce: "只減倉",
  pull: "撤單",
  hold: "暫停",
  kill: "終止",
};

export const VERDICT_LABELS: Record<string, string> = {
  OK: "通過",
  REDUCE_ONLY: "只准減倉",
  HOLD: "暫停",
  KILL: "終止",
};

export const SOURCE_LABELS: Record<string, string> = {
  model: "模型",
  fallback: "後備規則",
  risk: "風控",
  startup: "啟動中",
};

export const THRESHOLD_MODE_LABELS: Record<string, string> = {
  hybrid: "混合",
  zscore: "z 分數",
  absolute: "絕對值",
};

/**
 * Where the prices come from.
 *
 * Two adapters exist and they mean very different things: `synthetic` generates
 * the book locally (the instrument name is real, the price level is not), while
 * `hyperliquid` reads the live public book for a real perp. A reader comparing
 * the dashboard against an exchange needs to know which one they are looking at,
 * so the label is shown wherever a price is.
 */
export const VENUE_LABELS: Record<string, string> = {
  synthetic: "合成行情（模擬價格）",
  hyperliquid: "Hyperliquid 實時報價",
};

/** One line stating where the prices came from, per venue. */
export function sourceNote(venue: string, symbol: string, instrument: string | null): string {
  if (venue === "hyperliquid") {
    return `價格為 Hyperliquid 公共市場的實時報價${instrument ? `（${instrument} 永續合約）` : ""}，與 ${symbol} 在現貨市場的價格會有輕微基差。成交與帳戶損益都是模擬的：本系統不會送出任何真實訂單。`;
  }
  return `走勢為本機合成的模擬行情，與 ${symbol} 在真實交易所的價格無關 — 引擎不會讀取任何真實市場數據。可對照的是市場的形態（波動、成交、失衡），不是價格水平。`;
}

// ---------------------------------------------------------------------------
// Session control
// ---------------------------------------------------------------------------

export interface MarketOption {
  id: string;
  symbol: string;
  label: string;
}

/** A saved paper-trading run, as listed in the UI. */
export interface SavedSessionMeta {
  id: string;
  marketId: string;
  symbol: string;
  label: string;
  startedAtMs: number;
  savedAtMs: number;
  calibrationPath: string;
  stats: { decisions: number; fills: number; modelCalls: number; modelErrors: number };
  summary: {
    decisions: number;
    fills: number;
    equity: number;
    sessionPnl: number;
    realized: number;
    unrealized: number;
    fees: number;
    position: number;
    durationMs: number;
  };
}

export interface SessionInfo {
  running: boolean;
  sessionId: string | null;
  marketId: string | null;
  symbol: string | null;
  marketLabel: string | null;
  startedAtMs: number;
  markets: MarketOption[];
  saved: SavedSessionMeta[];
}

/**
 * The full payload from `GET /snapshot` and `GET /stream`.
 *
 * `loop` is null while stopped — the engine process stays up between sessions so
 * the dashboard can offer the market picker and the saved-run list instead of
 * panels full of stale numbers.
 */
export interface DashboardState {
  session: SessionInfo;
  loop: Snapshot | null;
}

export type ControlAction = "start" | "stop" | "save" | "delete" | "jev";

export interface ControlCommand {
  action: ControlAction;
  marketId?: string;
  resumeFrom?: string;
  id?: string;
  /** 雙模型閘門開關，for `jev`。 */
  enabled?: boolean;
}

export interface ControlResult {
  ok: boolean;
  message: string;
  sessionId?: string | null;
}
